<template>
  <b-modal
    ref="edit-engine-modal"
    title="Chỉnh sửa số máy"
    @ok="handleUpdateEngineNumber"
    @hide="resetModal"
    ok-title="Cập nhật"
    cancel-title="Hủy"
    :ok-disabled="isSubmitDisabled"
    size="md"
  >
    <template v-if="selectedItem">
      <!-- Product Info -->
      <b-card class="mb-3" border-variant="light">
        <template #header>
          <h6 class="mb-0">
            <i class="fas fa-info-circle mr-1"></i>
            Thông tin sản phẩm
          </h6>
        </template>
        <b-row>
          <b-col md="12">
            <small class="text-muted">Tên sản phẩm:</small>
            <div>
              <strong>{{ selectedItem.productName }}</strong>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col md="12">
            <small class="text-muted">Mã sản phẩm:</small>
            <div>{{ selectedItem.productCode }}</div>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col md="12">
            <small class="text-muted">Số khung:</small>
            <div>{{ selectedItem.chassisNumber }}</div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Engine Number Form -->
      <b-form-group label="Số máy hiện tại" label-for="current-engine">
        <b-form-input
          id="current-engine"
          :value="selectedItem.engineNumber"
          readonly
          class="bg-light"
        />
        <small class="text-muted">Số máy đang được sử dụng</small>
      </b-form-group>

      <b-form-group
        label="Số máy mới"
        label-for="new-engine"
        :invalid-feedback="formErrors.engineNumber"
        :state="getValidationState('engineNumber')"
      >
        <b-form-input
          id="new-engine"
          v-model="formData.engineNumber"
          type="text"
          placeholder="Nhập số máy mới"
          :state="getValidationState('engineNumber')"
          @input="clearError('engineNumber')"
        />
        <small class="text-muted">Nhập số máy mới để thay thế</small>
      </b-form-group>

      <div v-if="isLoading" class="text-center py-2">
        <b-spinner small class="mr-2" />
      </div>
    </template>
  </b-modal>
</template>

<script>
import productService from '@/core/services/product';
import { getToastInstance } from '@/utils/toastHelper';
import { handleApiError } from '../../../utils/errorHandler';

export default {
  name: 'EditEngineNumberModal',

  props: {
    productItem: {
      type: Object,
      default: () => null,
    },
  },

  data() {
    return {
      selectedItem: null,
      formData: {
        engineNumber: '',
      },
      formErrors: {
        engineNumber: '',
      },
      isLoading: false,
    };
  },

  computed: {
    isSubmitDisabled() {
      return (
        this.isLoading ||
        !this.formData.engineNumber.trim() ||
        this.hasErrors ||
        this.isSameAsOriginal
      );
    },

    hasErrors() {
      return Object.values(this.formErrors).some((error) => error !== '');
    },

    isSameAsOriginal() {
      return (
        this.selectedItem &&
        this.formData.engineNumber.trim() === this.selectedItem.engineNumber
      );
    },
  },

  watch: {
    productItem: {
      handler(newItem) {
        if (newItem) {
          this.selectedItem = { ...newItem };
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    ...getToastInstance(),

    showModal() {
      if (this.productItem) {
        this.selectedItem = { ...this.productItem };
        this.resetForm();
        this.$refs['edit-engine-modal'].show();
      }
    },

    hideModal() {
      this.$refs['edit-engine-modal'].hide();
    },

    resetModal() {
      this.resetForm();
    },

    /**
     * Reset form data
     */
    resetForm() {
      this.formData = {
        engineNumber: '',
      };
      this.clearAllErrors();
    },

    /**
     * Handle update engine number
     */
    async handleUpdateEngineNumber() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      try {
        const updateData = {
          id: this.selectedItem.id,
          engineNumber: this.formData.engineNumber.trim(),
        };

        await productService.updateEngineNumber(updateData);

        this.makeToastSuccess('Cập nhật số máy thành công!');
        this.$emit('engineNumberUpdated', {
          id: this.selectedItem.id,
          newEngineNumber: this.formData.engineNumber.trim(),
        });

        this.hideModal();
      } catch (error) {
        handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Validate entire form
     */
    validateForm() {
      this.clearAllErrors();

      if (!this.formData.engineNumber.trim()) {
        this.setError('engineNumber', 'Số máy không được để trống');
        return false;
      }

      if (this.formData.engineNumber.trim().length < 3) {
        this.setError('engineNumber', 'Số máy phải có ít nhất 3 ký tự');
        return false;
      }

      if (
        this.formData.engineNumber.trim() === this.selectedItem.engineNumber
      ) {
        this.setError(
          'engineNumber',
          'Số máy mới phải khác với số máy hiện tại',
        );
        return false;
      }

      return true;
    },

    getValidationState(field) {
      if (this.formErrors[field]) {
        return false;
      }
      return null;
    },

    /**
     * Error handling methods
     */
    setError(field, message) {
      this.$set(this.formErrors, field, message);
    },

    clearError(field) {
      this.$set(this.formErrors, field, '');
    },

    clearAllErrors() {
      Object.keys(this.formErrors).forEach((key) => {
        this.$set(this.formErrors, key, '');
      });
    },
  },
};
</script>

<style scoped>
.bg-light {
  background-color: #f8f9fa !important;
}

.text-muted {
  font-size: 0.875rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header h6 {
  color: #495057;
}

.text-center.py-2 {
  background-color: rgba(0, 123, 255, 0.1);
  border-radius: 0.25rem;
  border: 1px solid rgba(0, 123, 255, 0.2);
}

code {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}
</style>
